import React from 'react';
import { useNavigate } from 'react-router-dom';
import MetricCard from './MetricCard';

export const TechnicianCommandCenter = ({ stats, loading, activeCentreName }) => {
  const navigate = useNavigate();

  const phlebo = stats?.phlebotomy || {};
  const access = stats?.accession || {};
  const patho = stats?.pathology || {};

  return (
    <div className="lis-command-deck technician-deck">
      {/* 1. Laboratory Bench Operational Metrics */}
      <div className="lis-deck-section">
        <div className="lis-section-header-compact">
          <div className="lis-section-header-left">
            <span className="lis-section-kicker">BENCH & PHLEBOTOMY</span>
            <h3 className="lis-section-title">Specimen & Worklist Status</h3>
            <span className="lis-section-sub">&bull; {activeCentreName}</span>
          </div>
          <div className="lis-deck-actions">
            <button
              type="button"
              className="lis-deck-action-primary"
              onClick={() => navigate('/phlebotomy')}
            >
              Phlebotomy Queue
            </button>
            <button
              type="button"
              className="lis-deck-action-secondary"
              onClick={() => navigate('/sample-receive')}
            >
              Accessioning
            </button>
            <button
              type="button"
              className="lis-deck-quick-btn"
              onClick={() => navigate('/pathology-results')}
            >
              Result Entry
            </button>
          </div>
        </div>

        <div className="lis-kpi-grid">
          <MetricCard
            title="Awaiting Phlebotomy"
            value={phlebo.pendingCount ?? 0}
            subtitle="Specimen collection required"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/phlebotomy"
            variant={phlebo.pendingCount > 0 ? 'warning' : 'success'}
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            }
          />

          <MetricCard
            title="Collected Today"
            value={phlebo.collectedTodayCount ?? 0}
            subtitle="Tubes drawn & barcoded"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/phlebotomy"
            variant="info"
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            }
          />

          <MetricCard
            title="In-Transit / Unreceived"
            value={access.pendingReceiveCount ?? 0}
            subtitle="Awaiting central accession"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/sample-receive"
            variant={access.pendingReceiveCount > 0 ? 'warning' : 'success'}
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
                <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
              </svg>
            }
          />

          <MetricCard
            title="Accessioned Today"
            value={access.receivedTodayCount ?? 0}
            subtitle="Accepted into processing"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/sample-receive"
            variant="success"
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            }
          />

          <MetricCard
            title="Pending Result Entry"
            value={patho.pendingCount ?? 0}
            subtitle="Bench testing required"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/pathology-results"
            variant={patho.pendingCount > 0 ? 'warning' : 'default'}
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            }
          />

          <MetricCard
            title="Draft Results In-Progress"
            value={patho.draftCount ?? 0}
            subtitle="Saved but unfinalized"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/pathology-results"
            variant="info"
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            }
          />

          <MetricCard
            title="Critical Panic Alerts"
            value={patho.criticalTodayCount ?? 0}
            subtitle="Panic values flagged"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/pathology-results"
            variant={patho.criticalTodayCount > 0 ? 'danger' : 'default'}
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              </svg>
            }
          />

          <MetricCard
            title="Rejected Specimens"
            value={access.rejectedTodayCount ?? 0}
            subtitle="Redraw requested"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/sample-receive"
            variant={access.rejectedTodayCount > 0 ? 'danger' : 'default'}
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            }
          />
        </div>
      </div>

      {/* 2. My Work Queue Workstations */}
      <div className="lis-deck-section">
        <h4 className="lis-sub-section-title">Technician Workstations & Daily Action Queues</h4>
        <div className="lis-work-tasks-grid">
          <div className="lis-task-card" onClick={() => navigate('/phlebotomy')}>
            <div className="lis-task-badge warning">
              {phlebo.pendingCount ?? 0} DRAW REQUESTS
            </div>
            <h5 className="lis-task-title">Phlebotomy & Sample Collection</h5>
            <p className="lis-task-desc">
              Draw blood, verify patient identity with barcode, record collection timestamp, and dispatch tubes.
            </p>
            <span className="lis-task-link">Open Phlebotomy &rarr;</span>
          </div>

          <div className="lis-task-card" onClick={() => navigate('/sample-receive')}>
            <div className="lis-task-badge info">
              {access.pendingReceiveCount ?? 0} AWAITING RECEIVE
            </div>
            <h5 className="lis-task-title">Central Accessioning & Quality Check</h5>
            <p className="lis-task-desc">
              Verify specimen volume, inspect for hemolysis or lipemia, accept tubes into central processing or reject.
            </p>
            <span className="lis-task-link">Open Accessioning &rarr;</span>
          </div>

          <div className="lis-task-card" onClick={() => navigate('/pathology-results')}>
            <div className="lis-task-badge danger">
              {patho.pendingCount ?? 0} TESTS PENDING
            </div>
            <h5 className="lis-task-title">Pathology Result Entry & Delta Verification</h5>
            <p className="lis-task-desc">
              Record observation parameters, check prior historical patient trends, evaluate critical values and submit.
            </p>
            <span className="lis-task-link">Open Result Entry &rarr;</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicianCommandCenter;
